/**
 * 注册页面组件
 */
import { useState } from 'react';
import { Modal } from 'antd';
import { authAPI } from '../services/api';

// SVG 图标组件
const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const SparklesIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
  </svg>
);

const RegisterPage = ({ onRegisterSuccess }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    if (!username.trim()) {
      Modal.warning({ title: '提示', content: '请输入用户名' });
      return false;
    }
    
    if (!email.trim()) {
      Modal.warning({ title: '提示', content: '请输入邮箱' });
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Modal.warning({ title: '提示', content: '请输入有效的邮箱地址' });
      return false;
    }
    
    if (!password.trim()) {
      Modal.warning({ title: '提示', content: '请输入密码' });
      return false;
    }
    
    if (password.length < 6) {
      Modal.warning({ title: '提示', content: '密码长度至少6位' });
      return false;
    }
    
    if (password !== confirmPassword) {
      Modal.warning({ title: '提示', content: '两次输入的密码不一致' });
      return false;
    }
    
    return true;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await authAPI.register({ username, email, password });
      Modal.success({ 
        title: '注册成功', 
        content: '您已成功注册，请登录',
        onOk: () => {
          onRegisterSuccess();
        }
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '注册失败，请重试';
      Modal.error({ title: '注册失败', content: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center">
      {/* 背景装饰 */}
      <div className="fixed inset-0 tech-gradient-bg pointer-events-none" />
      
      <div className="relative z-10 w-full max-w-md px-4">
        <div className="glass-card p-8 rounded-2xl">
          {/* Logo 和标题 */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
              <SparklesIcon />
            </div>
            <h1 className="text-2xl font-bold gradient-text mb-2">巨猿人知识库问答系统</h1>
            <p className="text-gray-400">创建新账户</p>
          </div>

          {/* 注册表单 */}
          <form onSubmit={(e) => { e.preventDefault(); handleRegister(); }}>
            {/* 用户名输入 */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-medium mb-2">用户名</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="请输入用户名"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
            </div>

            {/* 邮箱输入 */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-medium mb-2">邮箱</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入邮箱地址"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
            </div>

            {/* 密码输入 */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-medium mb-2">密码</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码（至少6位）"
                  className="w-full px-4 py-3 pr-12 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {/* 确认密码输入 */}
            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium mb-2">确认密码</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="请再次输入密码"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
            </div>

            {/* 注册按钮 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '注册中...' : '注册'}
            </button>
          </form>

          {/* 登录链接 */}
          <div className="mt-6 text-center">
            <span className="text-gray-400">已有账户？</span>
            <button
              onClick={handleLogin}
              className="ml-2 text-blue-400 hover:text-blue-300 transition-colors font-medium"
            >
              立即登录
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
